import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAxios } from '../../common_nextgen/axios/AxiosHelper';
import FunctionKeys from '../../common_nextgen/common_components/FunctionKeys/FunctionKeys';
import useCustomNavigate from '../../common_nextgen/common_components/Navigation/NextGenNavigation';
import Form from '../../common_nextgen/common_components/form/Form';
import { useToast } from '../../common_nextgen/hooks/ToastContext';
import { PRICE_SCHEDULE_MODES, ROUTE_PATHS } from '../../common_nextgen/utils/Constants';
import { isAddMode, isEditMode, isReadOnlyMode } from "../../common_nextgen/utils/UtilFunctions";
import { formFields } from './CouponPatientPayGPIListFormConfig';

const screenInfo = {
    ADD: ['Add Coupon Patient Pay GPI List Detail', 'RCCPF002'],
    CHANGE: ['Coupon Patient Pay GPI List Detail', 'RCCPF003'],
    VIEW: ['Coupon Patient Pay GPI List Detail', 'RCCPF003I'],
};

const CouponPatientPayGPIListForm = ({ mode }) => {
    const { axios_helper_no_header } = useAxios();
    const location = useLocation();
    const [stateVal, setStateVal] = useState(location.state || []);
    const { currentState, previousState } = useMemo(() => {
        let [currentState = {}] = [...stateVal].reverse();
        const previousState = [...stateVal];
        previousState.splice(stateVal.length - 1, 1);
        return { previousState, currentState };
    }, [stateVal]);
    const {
        selectedSchedules = [],
        previousScreen = ROUTE_PATHS.COUPON_PATIENT_PAY,
        currentIndex: initialIndex = 0,
        externalMode: initialExternalMode = false,
    } = currentState;
    const navigateToPage = useCustomNavigate();
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [currentMode, setCurrentMode] = useState(mode);
    const [initialFormData, setInitialFormData] = useState({});
    const [constraintErrors, setConstraintErrors] = useState([]);
    const [externalMode, setExternalMode] = useState(initialExternalMode);
    const toast = useToast();

    const handleBack = () => {
        if (externalMode) {
            const message = {
                message: "navigate-to-external-program",
                url: previousScreen,
                parameters: { state: [...previousState] }
            };
            console.log("back messages", message);
            window.parent.postMessage(message, "*");
        } else if (currentIndex < selectedSchedules.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setCurrentMode(mode);
        } else {
            const lastScheduleName = selectedSchedules[currentIndex]?.cpfGpiListName || "";
            const updatedParamsData = { ...currentState.paramsData, cpfGpiListName: lastScheduleName };
            navigateToPage(previousScreen, [
                ...previousState,
                { ...currentState, paramsData: updatedParamsData, currentIndex }
            ]);
        }
    };

    const getPageTitle = () => {
        const [screenTitle, screenNumber] = isAddMode(mode) ? screenInfo.ADD : screenInfo.CHANGE;
        return (
            <>
                <span className="title">{screenTitle}</span>
                <span>{'-'}</span>
                <span className="screen-number">{screenNumber}</span>
            </>
        );
    };

    const submitForm = async (dataForSubmission) => {
        let url = '';
        let verb = '';
        let screenNumber = '';

        if (isAddMode(currentMode)) {
            url = 'api/couponPatPayGpiList';
            verb = 'POST';
            screenNumber = screenInfo.ADD[1];
        } else {
            url = 'api/couponPatPayGpiList';
            verb = 'PUT';
            screenNumber = screenInfo.CHANGE[1];

            if (JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)) {
                toast.current.show({ severity: 'info', summary: '', detail: 'No Change of Information', life: 3000 });
                return;
            }
        }

        const data = { ...dataForSubmission, screenNumber };

        try {
            const { responseStatus, responseData } = await axios_helper_no_header(verb, url, data, null);
            setInitialFormData(responseData);
            toast.current.show({ severity: 'success', summary: '', detail: responseStatus.message, life: 3000 });
            if (isAddMode(currentMode)) {
                setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
                const updatedCurrentState = {
                    ...currentState,
                    selectedSchedules: [{ ...responseData }],
                    currentIndex: 0,
                };
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_EDIT, [
                    ...previousState,
                    { ...updatedCurrentState }
                ]);
                setStateVal([
                    ...previousState,
                    { ...updatedCurrentState }
                ])
            }
        } catch (error) {
            const { responseStatus: { displayErrorFields = null, message } = {} } = error.data;
            if (displayErrorFields === null) {
                toast.current.show({ severity: 'error', summary: '', detail: message, life: 3000 });
            } else {
                setConstraintErrors(displayErrorFields);
            }
        }
    };

    const [keyFunc, keyName] = useMemo(() => {
        const keyFunc = {};
        const keyName = {};

        keyFunc.F12 = handleBack;
        keyName.F12 = 'Back';

        if (isEditMode(currentMode)) {
            keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
            keyName.chmod = 'View';
            keyFunc.F7 = () =>
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_GPI,
                    [...previousState,
                    { ...currentState, currentIndex, externalMode },
                    {
                        selectedSchedules: [selectedSchedules[currentIndex]],
                        currentIndex: 0,
                        previousScreen: location.pathname,
                    },]
                );
            keyName.F7 = 'GPIs';
            keyFunc.F8 = () =>
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_GPI_LIST_PLANS,
                    [...previousState,
                    { ...currentState, currentIndex, externalMode },
                    {
                        selectedSchedules: [selectedSchedules[currentIndex]],
                        currentIndex: 0,
                        previousScreen: location.pathname,
                    },]
                );
            keyName.F8 = 'Plans';
            keyFunc.F14 = () =>
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_GPI_LIST_NOTE,
                    [...previousState,
                    { ...currentState, currentIndex, externalMode },
                    {
                        selectedSchedules: [selectedSchedules[currentIndex]],
                        currentIndex: 0,
                        previousScreen: location.pathname,
                    },]
                );
            keyName.F14 = 'Note';
        } else if (isReadOnlyMode(currentMode)) {
            if(!externalMode) {
                keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
                keyName.chmod = 'Edit';
            }
            keyFunc.F7 = () =>
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_GPI,
                    [...previousState,
                    { ...currentState, currentIndex, externalMode },
                    {
                        selectedSchedules: [selectedSchedules[currentIndex]],
                        currentIndex: 0,
                        previousScreen: location.pathname,
                    },]
                );
            keyName.F7 = 'GPIs';
            keyFunc.F8 = () =>
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_GPI_LIST_PLANS,
                    [...previousState,
                    { ...currentState, currentIndex, externalMode },
                    {
                        selectedSchedules: [selectedSchedules[currentIndex]],
                        currentIndex: 0,
                        previousScreen: location.pathname,
                    },]
                );
            keyName.F8 = 'Plans';
            keyFunc.F14 = () =>
                navigateToPage(ROUTE_PATHS.COUPON_PATIENT_PAY_GPI_LIST_NOTE,
                    [...previousState,
                    { ...currentState, currentIndex, externalMode },
                    {
                        selectedSchedules: [selectedSchedules[currentIndex]],
                        currentIndex: 0,
                        previousScreen: location.pathname,
                    },]
                );
            keyName.F14 = 'Note';
        }

        return [keyFunc, keyName];
    }, [currentMode, currentIndex, initialFormData, externalMode]);

    useEffect(() => {
        const fetchInitialData = async () => {
            const url = isAddMode(currentMode)
                ? `api/couponPatPayGpiList/initialFormData/${screenInfo.ADD[1]}`
                : `api/couponPatPayGpiList/${encodeURIComponent(selectedSchedules[currentIndex].cpfGpiListName)}`;
            const { responseData } = await axios_helper_no_header('GET', url);
            setInitialFormData(responseData);
        };
        if (!isAddMode(currentMode) && !selectedSchedules?.[currentIndex]?.cpfGpiListName) return;
        fetchInitialData();
    }, [mode, currentIndex, currentMode, selectedSchedules[currentIndex]]);

    const handleMessage = (event) => {
        const { type, data } = event.data;
        console.log("iframe load", type, data);
        if (type === "set-params") {
            setStateVal(data);
            setExternalMode(true);
        }
        console.log("message", event.data);
    };

    useEffect(() => {
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    });


    return (
        <div className="grid container">
            <div className="col col-12">
                <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
            </div>
            <div className="col col-12">
                <h1>{getPageTitle()}</h1>
            </div>
            <div className="col col-12">
                <Form
                    formData={initialFormData}
                    formFields={formFields}
                    mode={currentMode}
                    submitFormData={submitForm}
                    constraintErrors={constraintErrors}
                />
            </div>
        </div>
    );
};

export default CouponPatientPayGPIListForm;